// DESCRIPTION: Admin event list table with sortable columns, status badges, and icon action buttons.
// DESCRIPTION: Uses design-system tokens (neutral/accent/semantic) — no hardcoded colors or gray-* classes.

import { __ } from '@wordpress/i18n';
import { useState } from '@wordpress/element';
import Pagination from './Pagination';
import { copyToClipboard } from '../utils/paymentHelpers';

/**
 * Event List Table component
 *
 * @since PBOOKING_SINCE
 *
 * @param {Object}      props
 * @param {Array}       props.events        - Array of event objects from the REST API.
 * @param {Function}    props.onDeleteEvent - Called with event ID when delete is confirmed.
 * @param {Function}    props.onCreateNew   - Called when "Add New Event" is clicked.
 * @param {Function}    props.onEditEvent   - Called with event ID when edit is clicked.
 * @param {Object|null} [props.pagination]  - Pagination state or null when single-page.
 * @param {boolean}     [props.isLoading]   - Whether a page fetch is in flight.
 */
const EventListTable = ( {
	events,
	onDeleteEvent,
	onCreateNew,
	onEditEvent,
	pagination = null,
	isLoading = false,
} ) => {
	const [ copiedEventId, setCopiedEventId ] = useState( null );

	/**
	 * Navigate to event edit page
	 *
	 * @since PBOOKING_SINCE
	 * @param {number} eventId
	 */
	const handleEditEvent = ( eventId ) => {
		window.location.href = `admin.php?page=nemtly-booking#/events/${ eventId }`;
	};

	/**
	 * Copy the [nemtly_event] shortcode for an event to the clipboard
	 *
	 * @since PBOOKING_SINCE
	 * @param {number} eventId
	 */
	const handleCopyShortcode = async ( eventId ) => {
		const success = await copyToClipboard(
			`[nemtly_event id=${ eventId }]`
		);
		if ( success ) {
			setCopiedEventId( eventId );
			setTimeout( () => setCopiedEventId( null ), 2000 );
		}
	};

	/**
	 * Navigate to event bookings page
	 *
	 * @since PBOOKING_SINCE
	 * @param {number} eventId
	 */
	const handleViewBookings = ( eventId ) => {
		window.location.href = `admin.php?page=nemtly-booking-bookings&event_id=${ eventId }`;
	};

	/**
	 * Duplicate an event and navigate to the copy
	 *
	 * @since PBOOKING_SINCE
	 * @param {number} eventId
	 */
	const handleDuplicateEvent = async ( eventId ) => {
		try {
			const response = await fetch(
				`${ window.nemtlyCreateEvent.restUrl }/events/${ eventId }/duplicate`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-WP-Nonce': window.nemtlyCreateEvent.nonce,
					},
				}
			);

			if ( ! response.ok ) {
				throw new Error( 'Failed to duplicate event' );
			}

			const data = await response.json();
			window.location.href = `admin.php?page=nemtly-booking#/events/${ data.id }`;
		} catch ( error ) {
			alert( error.message );
		}
	};

	/**
	 * Format a date string to a short human-readable date
	 *
	 * @since PBOOKING_SINCE
	 * @param {string} dateString
	 * @return {string}
	 */
	const formatDateOnly = ( dateString ) => {
		if ( ! dateString ) {
			return '—';
		}
		return new Date( dateString ).toLocaleDateString( 'en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		} );
	};

	/**
	 * Format an H:MM time string to a 12-hour display
	 *
	 * @since PBOOKING_SINCE
	 * @param {string} timeString
	 * @return {string}
	 */
	const formatTimeOnly = ( timeString ) => {
		if ( ! timeString ) {
			return '';
		}
		let hour, minute;
		if ( timeString.includes( ':' ) ) {
			const parts = timeString.split( ':' );
			hour = parseInt( parts[ 0 ], 10 );
			minute = parseInt( parts[ 1 ], 10 );
		} else {
			const date = new Date( timeString );
			hour = date.getHours();
			minute = date.getMinutes();
		}
		const d = new Date();
		d.setHours( hour, minute );
		return d.toLocaleTimeString( 'en-US', {
			hour: '2-digit',
			minute: '2-digit',
			hour12: true,
		} );
	};

	/**
	 * Booking-status badge (open / closed / full)
	 *
	 * @since PBOOKING_SINCE
	 * @param {Object} event
	 * @return {JSX.Element}
	 */
	const getStatusBadge = ( event ) => {
		const badgeBase =
			'nb-inline-flex nb-items-center nb-gap-1 nb-px-2 nb-py-0.5 nb-rounded-full nb-text-xs nb-font-medium';

		switch ( event.booking_status ) {
			case 'closed':
				return (
					<span
						className={ `${ badgeBase } nb-bg-neutral-200 nb-text-neutral-700` }
					>
						<span className="nb-w-1.5 nb-h-1.5 nb-rounded-full nb-bg-neutral-500 nb-shrink-0" />
						{ __( 'Closed', 'nemtly-booking' ) }
					</span>
				);
			case 'full':
				return (
					<span
						className={ `${ badgeBase } nb-bg-warning-50 nb-text-warning-700` }
					>
						<span className="nb-w-1.5 nb-h-1.5 nb-rounded-full nb-bg-warning-500 nb-shrink-0" />
						{ __( 'Full', 'nemtly-booking' ) }
					</span>
				);
			default:
				return (
					<span
						className={ `${ badgeBase } nb-bg-success-50 nb-text-success-700` }
					>
						<span className="nb-w-1.5 nb-h-1.5 nb-rounded-full nb-bg-success-500 nb-shrink-0" />
						{ __( 'Open', 'nemtly-booking' ) }
					</span>
				);
		}
	};

	/**
	 * Post-status badge (publish / draft / future / trash)
	 *
	 * @since PBOOKING_SINCE
	 * @param {string} status
	 * @return {JSX.Element}
	 */
	const getPostStatusBadge = ( status ) => {
		const badgeBase =
			'nb-inline-flex nb-items-center nb-px-2 nb-py-0.5 nb-rounded nb-text-xs nb-font-medium';

		switch ( status ) {
			case 'publish':
				return (
					<span
						className={ `${ badgeBase } nb-bg-success-50 nb-text-success-700` }
					>
						{ __( 'Published', 'nemtly-booking' ) }
					</span>
				);
			case 'draft':
				return (
					<span
						className={ `${ badgeBase } nb-bg-neutral-200 nb-text-neutral-700` }
					>
						{ __( 'Draft', 'nemtly-booking' ) }
					</span>
				);
			case 'future':
				return (
					<span
						className={ `${ badgeBase } nb-bg-info-50 nb-text-info-700` }
					>
						{ __( 'Scheduled', 'nemtly-booking' ) }
					</span>
				);
			case 'trash':
				return (
					<span
						className={ `${ badgeBase } nb-bg-danger-50 nb-text-danger-700` }
					>
						{ __( 'Trashed', 'nemtly-booking' ) }
					</span>
				);
			default:
				return (
					<span
						className={ `${ badgeBase } nb-bg-neutral-200 nb-text-neutral-700` }
					>
						{ status }
					</span>
				);
		}
	};

	/**
	 * Event type badge
	 *
	 * @since PBOOKING_SINCE
	 * @param {Object} event
	 * @return {JSX.Element}
	 */
	const getEventTypeBadge = ( event ) => {
		const badgeBase =
			'nb-inline-flex nb-items-center nb-gap-1 nb-px-2 nb-py-0.5 nb-rounded nb-text-xs nb-font-medium';

		if ( event.is_recurring === '1' || event.is_recurring === true ) {
			return (
				<span
					className={ `${ badgeBase } nb-bg-accent-50 nb-text-neutral-900` }
				>
					<svg
						className="nb-w-3 nb-h-3 nb-shrink-0"
						fill="none"
						viewBox="0 0 12 12"
					>
						<path
							d="M10 6A4 4 0 1 1 6 2"
							stroke="currentColor"
							strokeWidth="1.2"
							strokeLinecap="round"
						/>
						<path
							d="M6 1l2 1-2 1"
							stroke="currentColor"
							strokeWidth="1.2"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
					{ __( 'Recurring', 'nemtly-booking' ) }
				</span>
			);
		}

		switch ( event.event_type ) {
			case 'group':
				return (
					<span
						className={ `${ badgeBase } nb-bg-info-50 nb-text-info-700` }
					>
						<svg
							className="nb-w-3 nb-h-3 nb-shrink-0"
							fill="none"
							viewBox="0 0 12 12"
						>
							<circle
								cx="4"
								cy="4"
								r="1.5"
								stroke="currentColor"
								strokeWidth="1.2"
							/>
							<circle
								cx="8"
								cy="4"
								r="1.5"
								stroke="currentColor"
								strokeWidth="1.2"
							/>
							<path
								d="M1 10c0-1.657 1.343-3 3-3M8 7c1.657 0 3 1.343 3 3"
								stroke="currentColor"
								strokeWidth="1.2"
								strokeLinecap="round"
							/>
						</svg>
						{ __( 'Group', 'nemtly-booking' ) }
					</span>
				);
			case 'event':
				return (
					<span
						className={ `${ badgeBase } nb-bg-neutral-50 nb-text-neutral-700 nb-border nb-border-neutral-200` }
					>
						<svg
							className="nb-w-3 nb-h-3 nb-shrink-0"
							fill="none"
							viewBox="0 0 12 12"
						>
							<rect
								x="1"
								y="2"
								width="10"
								height="9"
								rx="1"
								stroke="currentColor"
								strokeWidth="1.2"
							/>
							<path
								d="M8 1v2M4 1v2M1 5h10"
								stroke="currentColor"
								strokeWidth="1.2"
								strokeLinecap="round"
							/>
						</svg>
						{ __( 'Event', 'nemtly-booking' ) }
					</span>
				);
			default:
				return (
					<span
						className={ `${ badgeBase } nb-bg-neutral-50 nb-text-neutral-700 nb-border nb-border-neutral-200` }
					>
						{ __( 'One-time', 'nemtly-booking' ) }
					</span>
				);
		}
	};

	if ( ! events || events.length === 0 ) {
		return (
			<div className="nb-flex nb-flex-col nb-items-center nb-justify-center nb-py-20 nb-text-center nb-bg-neutral-0 nb-rounded-xl nb-border nb-border-neutral-200">
				<p className="nb-text-sm nb-text-neutral-500 nb-mb-4">
					{ __( 'No events found.', 'nemtly-booking' ) }
				</p>
				<button
					type="button"
					className="nemtly-button-primary"
					onClick={ onCreateNew }
				>
					{ __( 'Add New Event', 'nemtly-booking' ) }
				</button>
			</div>
		);
	}

	return (
		<div className="nb-bg-neutral-0 nb-rounded-xl nb-border nb-border-neutral-200 nb-shadow-elevation-1 nb-overflow-hidden">
			{ /* Table header row */ }
			<div className="nb-flex nb-items-center nb-justify-between nb-px-5 nb-py-4 nb-border-b nb-border-neutral-200">
				<div>
					<h2 className="nb-text-sm nb-font-semibold nb-text-neutral-900">
						{ __( 'Events', 'nemtly-booking' ) }
					</h2>
					<p className="nb-text-xs nb-text-neutral-500 nb-mt-0.5">
						{ __(
							'Manage, edit, or remove your events.',
							'nemtly-booking'
						) }
					</p>
				</div>
				<button
					type="button"
					onClick={ onCreateNew }
					className="nemtly-button-primary nemtly-button-sm"
				>
					{ __( 'Add New Event', 'nemtly-booking' ) }
				</button>
			</div>

			{ /* Scrollable table wrapper */ }
			<div className="nb-relative nb-overflow-x-auto">
				{ /* Loading shimmer overlay */ }
				{ isLoading && (
					<div className="nb-absolute nb-inset-0 nb-bg-neutral-0/70 nb-flex nb-items-center nb-justify-center nb-z-10">
						<div className="nb-w-6 nb-h-6 nb-rounded-full nb-border-2 nb-border-neutral-200 nb-border-t-accent-500 nb-animate-spin" />
					</div>
				) }

				<table className="nb-min-w-full nb-text-sm nb-border-collapse">
					<thead>
						<tr className="nb-border-b nb-border-neutral-200 nb-bg-neutral-50">
							<th
								scope="col"
								className="nb-py-3 nb-pl-5 nb-pr-3 nb-text-left nb-text-xs nb-font-semibold nb-text-neutral-500 nb-uppercase nb-tracking-wide nb-whitespace-nowrap"
							>
								{ __( 'Event', 'nemtly-booking' ) }
							</th>
							<th
								scope="col"
								className="nb-px-3 nb-py-3 nb-text-left nb-text-xs nb-font-semibold nb-text-neutral-500 nb-uppercase nb-tracking-wide nb-whitespace-nowrap"
							>
								{ __( 'Date & Time', 'nemtly-booking' ) }
							</th>
							<th
								scope="col"
								className="nb-px-3 nb-py-3 nb-text-left nb-text-xs nb-font-semibold nb-text-neutral-500 nb-uppercase nb-tracking-wide nb-whitespace-nowrap"
							>
								{ __( 'Bookings', 'nemtly-booking' ) }
							</th>
							<th
								scope="col"
								className="nb-px-3 nb-py-3 nb-text-left nb-text-xs nb-font-semibold nb-text-neutral-500 nb-uppercase nb-tracking-wide nb-whitespace-nowrap"
							>
								{ __( 'Booking Status', 'nemtly-booking' ) }
							</th>
							<th
								scope="col"
								className="nb-px-3 nb-py-3 nb-text-left nb-text-xs nb-font-semibold nb-text-neutral-500 nb-uppercase nb-tracking-wide nb-whitespace-nowrap"
							>
								{ __( 'Type', 'nemtly-booking' ) }
							</th>
							<th
								scope="col"
								className="nb-px-3 nb-py-3 nb-text-left nb-text-xs nb-font-semibold nb-text-neutral-500 nb-uppercase nb-tracking-wide nb-whitespace-nowrap"
							>
								{ __( 'Status', 'nemtly-booking' ) }
							</th>
							<th
								scope="col"
								className="nb-py-3 nb-pl-3 nb-pr-5 nb-text-right"
							>
								<span className="nb-sr-only">
									{ __( 'Actions', 'nemtly-booking' ) }
								</span>
							</th>
						</tr>
					</thead>

					<tbody className="nb-divide-y nb-divide-neutral-200">
						{ events.map( ( event ) => (
							<tr
								key={ event.id }
								className="nb-group hover:nb-bg-neutral-50 nb-transition-colors nb-duration-fast"
							>
								{ /* Event title + location */ }
								<td className="nb-py-3.5 nb-pl-5 nb-pr-3 nb-align-top">
									<button
										type="button"
										className="nb-text-left nb-font-medium nb-text-neutral-900 hover:nb-text-accent-500 hover:nb-underline nb-transition-colors nb-duration-fast nb-cursor-pointer nb-bg-transparent nb-border-0 nb-p-0"
										onClick={ () =>
											onEditEvent( event.id )
										}
									>
										{ event.title?.rendered || event.title }
									</button>
									{ event.location && (
										<div className="nb-flex nb-items-center nb-gap-1 nb-mt-0.5">
											<svg
												className="nb-w-3 nb-h-3 nb-text-neutral-400 nb-shrink-0"
												fill="none"
												viewBox="0 0 12 12"
											>
												<path
													d="M6 1C4.343 1 3 2.343 3 4c0 2.5 3 7 3 7s3-4.5 3-7c0-1.657-1.343-3-3-3z"
													stroke="currentColor"
													strokeWidth="1.2"
												/>
												<circle
													cx="6"
													cy="4"
													r="1"
													fill="currentColor"
												/>
											</svg>
											<span className="nb-text-xs nb-text-neutral-500">
												{ event.location }
											</span>
										</div>
									) }

									{ /* Embed shortcode + copy */ }
									<div className="nb-flex nb-items-center nb-gap-1.5 nb-mt-1.5">
										<code className="nb-text-xs nb-text-neutral-500 nb-bg-neutral-50 nb-border nb-border-neutral-200 nb-rounded nb-px-1.5 nb-py-0.5 nb-font-mono nb-whitespace-nowrap">
											{ `[nemtly_event id=${ event.id }]` }
										</code>
										<button
											type="button"
											className="nb-p-1 nb-rounded nb-text-neutral-400 hover:nb-text-neutral-900 hover:nb-bg-neutral-200 nb-transition-colors nb-duration-fast nb-shrink-0"
											onClick={ () =>
												handleCopyShortcode( event.id )
											}
											title={
												copiedEventId === event.id
													? __(
															'Copied!',
															'nemtly-booking'
													  )
													: __(
															'Copy shortcode',
															'nemtly-booking'
													  )
											}
										>
											{ copiedEventId === event.id ? (
												<svg
													className="nb-w-3.5 nb-h-3.5 nb-text-success-500"
													fill="none"
													viewBox="0 0 16 16"
												>
													<path
														d="M3 8.5l3 3 7-7"
														stroke="currentColor"
														strokeWidth="1.25"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
											) : (
												<svg
													className="nb-w-3.5 nb-h-3.5"
													fill="none"
													viewBox="0 0 16 16"
												>
													<rect
														x="5"
														y="5"
														width="8"
														height="9"
														rx="1.25"
														stroke="currentColor"
														strokeWidth="1.25"
													/>
													<path
														d="M3 11V3a1 1 0 0 1 1-1h7"
														stroke="currentColor"
														strokeWidth="1.25"
														strokeLinecap="round"
													/>
												</svg>
											) }
										</button>
									</div>
								</td>

								{ /* Date & time */ }
								<td className="nb-px-3 nb-py-3.5 nb-align-top nb-whitespace-nowrap">
									<span className="nb-text-neutral-700">
										{ formatDateOnly( event.event_date ) }
									</span>
									{ event.event_time && (
										<div className="nb-text-xs nb-text-neutral-500 nb-mt-0.5">
											{ formatTimeOnly(
												event.event_time
											) }
										</div>
									) }
									{ event.timezone && (
										<div className="nb-text-xs nb-text-neutral-400">
											{ event.timezone }
										</div>
									) }
									{ event.end_date &&
										event.end_date !== event.event_date && (
											<div className="nb-text-xs nb-text-neutral-400 nb-mt-0.5">
												→{ ' ' }
												{ formatDateOnly(
													event.end_date
												) }
											</div>
										) }
								</td>

								{ /* Attendees */ }
								<td className="nb-px-3 nb-py-3.5 nb-align-top nb-whitespace-nowrap">
									<span className="nb-text-neutral-700 nb-font-medium">
										{ event.confirmed_bookings || 0 }
									</span>
									{ event.capacity && (
										<span className="nb-text-neutral-400">
											{ ' ' }
											/ { event.capacity }
										</span>
									) }
								</td>

								{ /* Booking status */ }
								<td className="nb-px-3 nb-py-3.5 nb-align-top nb-whitespace-nowrap">
									{ getStatusBadge( event ) }
								</td>

								{ /* Event type */ }
								<td className="nb-px-3 nb-py-3.5 nb-align-top nb-whitespace-nowrap">
									{ getEventTypeBadge( event ) }
								</td>

								{ /* Post status */ }
								<td className="nb-px-3 nb-py-3.5 nb-align-top nb-whitespace-nowrap">
									{ getPostStatusBadge( event.status ) }
								</td>

								{ /* Actions */ }
								<td className="nb-py-3.5 nb-pl-3 nb-pr-5 nb-align-top nb-whitespace-nowrap">
									<div className="nb-flex nb-items-center nb-justify-end nb-gap-1 nb-opacity-40 group-hover:nb-opacity-100 nb-transition-opacity nb-duration-fast">
										{ /* Edit */ }
										<button
											type="button"
											className="nb-p-1.5 nb-rounded-md nb-text-neutral-500 hover:nb-text-neutral-900 hover:nb-bg-neutral-200 nb-transition-colors nb-duration-fast"
											onClick={ () =>
												onEditEvent( event.id )
											}
											title={ __(
												'Edit event',
												'nemtly-booking'
											) }
										>
											<svg
												className="nb-w-4 nb-h-4"
												fill="none"
												viewBox="0 0 16 16"
											>
												<path
													d="M11.5 2.5a1.414 1.414 0 0 1 2 2L5 13H3v-2L11.5 2.5z"
													stroke="currentColor"
													strokeWidth="1.25"
													strokeLinejoin="round"
												/>
											</svg>
										</button>

										{ /* Duplicate */ }
										<button
											type="button"
											className="nb-p-1.5 nb-rounded-md nb-text-neutral-500 hover:nb-text-neutral-900 hover:nb-bg-neutral-200 nb-transition-colors nb-duration-fast"
											onClick={ () =>
												handleDuplicateEvent( event.id )
											}
											title={ __(
												'Duplicate event',
												'nemtly-booking'
											) }
										>
											<svg
												className="nb-w-4 nb-h-4"
												fill="none"
												viewBox="0 0 16 16"
											>
												<rect
													x="5"
													y="5"
													width="8"
													height="9"
													rx="1.25"
													stroke="currentColor"
													strokeWidth="1.25"
												/>
												<path
													d="M3 11V3a1 1 0 0 1 1-1h7"
													stroke="currentColor"
													strokeWidth="1.25"
													strokeLinecap="round"
												/>
											</svg>
										</button>

										{ /* View bookings */ }
										<button
											type="button"
											className="nb-p-1.5 nb-rounded-md nb-text-neutral-500 hover:nb-text-neutral-900 hover:nb-bg-neutral-200 nb-transition-colors nb-duration-fast"
											onClick={ () =>
												handleViewBookings( event.id )
											}
											title={ __(
												'View bookings',
												'nemtly-booking'
											) }
										>
											<svg
												className="nb-w-4 nb-h-4"
												fill="none"
												viewBox="0 0 16 16"
											>
												<path
													d="M2 4h12M2 8h8M2 12h5"
													stroke="currentColor"
													strokeWidth="1.25"
													strokeLinecap="round"
												/>
											</svg>
										</button>

										{ /* Delete */ }
										<button
											type="button"
											className="nb-p-1.5 nb-rounded-md nb-text-neutral-500 hover:nb-text-danger-500 hover:nb-bg-danger-50 nb-transition-colors nb-duration-fast"
											onClick={ () =>
												onDeleteEvent( event.id )
											}
											title={ __(
												'Delete event',
												'nemtly-booking'
											) }
										>
											<svg
												className="nb-w-4 nb-h-4"
												fill="none"
												viewBox="0 0 16 16"
											>
												<path
													d="M3 5h10M6 5V3h4v2M7 8v4M9 8v4"
													stroke="currentColor"
													strokeWidth="1.25"
													strokeLinecap="round"
												/>
												<path
													d="M4 5l.75 8.25A1 1 0 0 0 5.74 14h4.52a1 1 0 0 0 .99-.75L12 5"
													stroke="currentColor"
													strokeWidth="1.25"
													strokeLinecap="round"
													strokeLinejoin="round"
												/>
											</svg>
										</button>
									</div>
								</td>
							</tr>
						) ) }
					</tbody>
				</table>
			</div>

			{ /* Pagination */ }
			{ pagination && (
				<div className="nb-border-t nb-border-neutral-200 nb-px-5 nb-py-3">
					<Pagination
						currentPage={ pagination.currentPage }
						totalPages={ pagination.totalPages }
						totalItems={ pagination.totalItems }
						itemsPerPage={ pagination.itemsPerPage }
						onPageChange={ pagination.onPageChange }
						onItemsPerPageChange={ pagination.onItemsPerPageChange }
						disabled={ isLoading }
						showInfo={ true }
						showItemsPerPage={ true }
					/>
				</div>
			) }
		</div>
	);
};

export default EventListTable;
